import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { formatDateTime, handleDownload } from '@/utils/common';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, Row, Table, TableColumnsType, Tabs, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getHistoryFood,
  importFile,
  watchFoodMessage,
  watchFoodPaginationHistory,
  watchListHistoryFood,
  watchFoodLoading,
  resetMessages,
  downloadTemplateFood
} from '@/redux/slices/food.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { Form } from 'antd';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
};

export default function FoodQuantificationByGroupTable() {
  const [activeTab, setActiveTab] = useState<string>('INPUT');
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const { push } = useQuery();
  const [fileName, setFileName] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messageInfo = useAppSelector(watchFoodMessage);
  const loading = useAppSelector(watchFoodLoading);
  const listHistory = useAppSelector(watchListHistoryFood);
  const pagination = useAppSelector(watchFoodPaginationHistory);

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
    }: PageChangeParams) => {
      dispatch(getHistoryFood({
        offset: page,
        limit: pageSize
      }));
    },
    [dispatch]
  );

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    await dispatch(importFile(formData));
    await dispatch(getHistoryFood({}));
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      setFileName(file.name);
      setSelectedFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      setSelectedFile(file);
    }
  };

  const handleDelFile = () => {
    setFile(null);
  }


  const handleGoBack = useCallback(() => {
    push(PageURLs.ofFoodManagement());
  }, [push]);

  const handleCancel = () => {
    setFileName(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    dispatch(getHistoryFood({}));
  }, [dispatch]);


  const handleDownLoadTemplateFood = async () => {
    const template = await dispatch(downloadTemplateFood()).unwrap();
    if (template) {
      handleDownload(template.URL, template.NAME);
    }
  }

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      if (messageInfo.type === 'success') {
        handleCancel();
      }
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  const tabItems = [
    {
      key: 'INPUT',
      label: 'Nhập dữ liệu'
    },
    {
      key: 'HISTORY',
      label: 'Lịch sử nhập dữ liệu'
    }
  ];

  const columns: TableColumnsType<any> = [
    {
      title: 'STT',
      render: (_, __, index) => index + 1,
      width: 100
    },
    {
      title: 'Tên file',
      dataIndex: 'name',
      key: 'name',
      width: 400
    },
    {
      title: 'Tải xuống',
      dataIndex: 'url',
      key: 'url',
      render: (url: string, record: any) => {
        return (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;

              .ant-btn {
                border: none;
              }
            `}
          >
            <Button
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(url, record.fileName)}
            />
          </div>
        );
      },
      width: 100
    },
    {
      title: 'Ngày nhập dữ liệu',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 200,
      render: (record) => (record ? formatDateTime(record) : '')
    },
    {
      title: 'Người nhập',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 200
    }
  ];

  return (
    <>
      <Form form={form} css={rootStyles}>
        <div css={rootStyles}>
          <Row css={boxStyles} align='middle' gutter={16}>
            <Button
              type='text'
              icon={<ArrowLeftOutlined />}
              onClick={handleGoBack}
            ></Button>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              Nhập giá trị dinh dưỡng của thực phẩm theo danh sách
            </Typography.Title>
          </Row>

          <Tabs
            css={tabStyle}
            defaultActiveKey='INPUT'
            onChange={(key) => setActiveTab(key)}
          >
            {tabItems.map((item) => (
              <Tabs.TabPane tab={item.label} key={item.key} />
            ))}
          </Tabs>

          {activeTab === 'INPUT' && (
            <>
              <div css={boxStyles}>
                <Row>
                  <Button type='primary' icon={<UploadOutlined />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Tải lên
                  </Button>
                  <Button type='link'
                    onClick={() => handleDownLoadTemplateFood()}
                  >
                    Tải xuống template
                  </Button>
                </Row>
                <Row>
                  <Typography.Text
                  >
                    {file?.name}
                  </Typography.Text>
                  <DeleteOutlined
                    hidden={!file}
                    style={{ fontSize: 23 }}
                    onClick={() => handleDelFile()}
                  />
                </Row>
              </div>
              <div css={boxStyles}>
                <label
                  htmlFor="file-upload"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <div
                    style={{
                      minHeight: '60vh',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Typography.Title level={5}>
                      {fileName ? `Đã chọn: ${fileName}` : 'Vui lòng kéo thả hoặc chọn thư mục dữ liệu danh sách thực phẩm'}
                    </Typography.Title>
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                </label>
              </div>
              <div css={styleFooter}>
                <Row align='top' gutter={16}>
                  <Button
                    type='default'
                    size='large'
                    onClick={handleCancel}
                  >
                    Hủy
                  </Button>

                  <Form.Item noStyle>
                    <Button type='primary'
                      size='large'
                      htmlType='submit'
                      onClick={handleUpload}
                      loading={loading}
                      style={{ marginLeft: 20 }}
                    >
                      Lưu
                    </Button>
                  </Form.Item>
                </Row>
              </div>
            </>
          )}
          {activeTab === 'HISTORY' && (
            <Table<any>
              columns={columns}
              dataSource={listHistory}
              pagination={{
                showQuickJumper: false,
                showSizeChanger: true,
                current: pagination.curPage,
                pageSize: pagination.limitPage,
                total: pagination.totalRows,
                onChange: (page, pageSize) => {
                  handlePageChange({ page, pageSize });
                }
              }}
            />
          )}
        </div>
      </Form>
    </>
  );
}

const styleFooter = css`
  min-height: calc(12vh - var(--header-height));
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 0 1rem;
  background-color: var(--white-color);
  .ant-btn-lg {
    width: 30rem;
  }
`;

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
  min-height: calc(100vh - var(--header-height));

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const tabStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 6px 8px;
    color: #333;
    border: 1px solid #ccc;
    background-color: #f0f0f0;
  }

  .ant-tabs-tab-active {
    background-color: var(--blue-dark-color);
    border: 1px solid var(--blue-dark-color);

    .ant-tabs-tab-btn {
      color: #ffffff !important;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-tab:hover {
    color: #333 !important;
  }

  .ant-tabs-tab:not(:first-of-type) {
    margin: 0 0 0 8px !important;
  }
`;
